/**
 * Session context.
 *
 * Thin on purpose. Every decision it makes lives in `session.ts`, which is pure
 * and tested; this file only fetches, subscribes, and puts the answer on the
 * document.
 */

import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from 'react';
import { supabase } from '../lib/supabase.js';
import {
  permissionsFor, resolveSession, type Membership, type ResolvedSession,
  type SessionOutcome, type Shop,
} from './session.js';
import { createTranslator, type Translator, type OverrideMap } from '../intl/i18n.js';
import { COUNTRIES, formatMoney as fmtMoney, formatHours as fmtHours } from '../intl/locale.js';
import { DICTIONARIES } from '../intl/dictionaries.js';

const PREFERRED_SHOP_KEY = 'estimator.shop';

interface SessionContextValue {
  outcome: SessionOutcome;
  loading: boolean;
  session: ResolvedSession | null;
  t: Translator;
  money: (n: number) => string;
  hours: (n: number) => string;
  setLanguage: (code: string) => Promise<void>;
  /** Set when a language change did not reach the server. A preference that
   *  silently reverts looks like the app ignoring you. */
  languageError: string | null;
  switchShop: (shopId: string) => void;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside <SessionProvider>');
  return ctx;
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [outcome, setOutcome] = useState<SessionOutcome>({ kind: 'signed_out' });
  const [loading, setLoading] = useState(true);
  const [languageError, setLanguageError] = useState<string | null>(null);
  const [overrides, setOverrides] = useState<OverrideMap>({});
  const [preferredShopId, setPreferredShopId] = useState<string | null>(
    () => { try { return localStorage.getItem(PREFERRED_SHOP_KEY); } catch { return null; } },
  );

  const load = useCallback(async () => {
    // Preview mode: render the signed-in app with a fixture session and no
    // database. Design and layout work — and screenshots — should not require
    // standing up Supabase. Never enabled in a production build.
    if (import.meta.env.VITE_PREVIEW === '1') {
      // TWO shops, in two countries, deliberately. One shop can never exercise
      // the currency switch, and - more importantly - can never show the
      // manual-vehicle-entry path that every market outside North America uses.
      const shops = [
        { id: 'preview-shop', name: 'Riverbend Collision', countryCode: 'CA',
          defaultLanguage: 'en', status: 'active' as const },
        { id: 'preview-shop-ae', name: 'Al Quoz Auto Body', countryCode: 'AE',
          defaultLanguage: 'ar', status: 'active' as const },
      ];
      const shop = shops.find((x) => x.id === preferredShopId) ?? shops[0]!;
      setOutcome({
        kind: 'ready',
        session: {
          user: { id: 'preview', email: 'sofia@riverbendcollision.ca', language: null },
          shop,
          role: 'estimator',
          country: COUNTRIES[shop.countryCode]!,
          language: 'en',
          dir: 'ltr',
          availableShops: shops,
          can: permissionsFor('estimator'),
        },
      });
      setLoading(false);
      return;
    }

    const { data: auth } = await supabase.auth.getUser();
    const authUser = auth.user;
    if (!authUser) {
      setOutcome({ kind: 'signed_out' });
      setLoading(false);
      return;
    }

    // ONE round trip, not three. `platform` is not exposed over the API at all;
    // this security-definer function is the only door, and it filters on
    // auth.uid() internally — see supabase/migrations/..._session_bootstrap.sql
    // and src/db/session-rpc.test.ts, which is what proves it isolates.
    // `.schema('public')` matters: the client defaults to `app`, and without
    // this the call looks for `app.session_bootstrap`, which does not exist.
    // The functions live in `public` because that is the only schema besides
    // `app` that PostgREST exposes.
    const { data: boot, error: bootError } =
      await supabase.schema('public').rpc('session_bootstrap');

    if (bootError) {
      // A failure to READ a session is not the same as having none. Signing the
      // user out here would look, to them, like being logged out at random on a
      // bad connection — so the error is surfaced instead.
      setOutcome({ kind: 'load_failed', message: bootError.message });
      setLoading(false);
      return;
    }

    const payload = (boot ?? {}) as {
      user: { id: string; email: string; language: string | null } | null;
      memberships: Array<{ shop_id: string; role: string; status: string }>;
      shops: Array<{ id: string; name: string; country_code: string;
                     default_language: string; status: string }>;
    };

    const resolved = resolveSession({
      user: {
        id: authUser.id,
        email: payload.user?.email ?? authUser.email ?? '',
        language: payload.user?.language ?? null,
      },
      memberships: (payload.memberships ?? []).map((m): Membership => ({
        shopId: m.shop_id, role: m.role as Membership['role'],
        status: m.status as Membership['status'],
      })),
      shops: (payload.shops ?? []).map((s): Shop => ({
        id: s.id, name: s.name, countryCode: s.country_code,
        defaultLanguage: s.default_language, status: s.status as Shop['status'],
      })),
      preferredShopId,
    });

    setOutcome(resolved);

    if (resolved.kind === 'ready') {
      const { data: rows } = await supabase.from('translation_overrides')
        .select('language,key,value').eq('shop_id', resolved.session.shop.id);
      const map: OverrideMap = {};
      for (const r of rows ?? []) {
        (map[r.language] ??= {})[r.key] = r.value;
      }
      setOverrides(map);
    }
    setLoading(false);
  }, [preferredShopId]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    const { data } = supabase.auth.onAuthStateChange(() => { void load(); });
    return () => data.subscription.unsubscribe();
  }, [load]);

  const session = outcome.kind === 'ready' ? outcome.session : null;
  const lang = session?.language ?? 'en';

  const t = useMemo(
    () => createTranslator({ lang, dictionaries: DICTIONARIES, overrides }),
    [lang, overrides],
  );

  // The only place `dir` is set. Arabic is a layout change, not a translation,
  // so it belongs on the document rather than in a hundred components.
  useEffect(() => {
    document.documentElement.setAttribute('lang', lang);
    document.documentElement.setAttribute('dir', t.dir);
  }, [lang, t.dir]);

  const money = useCallback(
    (n: number) => (session ? fmtMoney(n, session.country) : ''),
    [session],
  );
  const hours = useCallback(
    (n: number) => (session ? fmtHours(n, session.country) : ''),
    [session],
  );

  const setLanguage = useCallback(async (code: string) => {
    if (!session) return;
    const previous = session.language;
    setLanguageError(null);
    // Optimistic: the globe should feel instant at a vehicle on a bad connection.
    setOutcome({ kind: 'ready', session: { ...session, language: code } });

    // Preview has no database. Without this the RPC fails, `load()` puts the
    // old language straight back, and the one mode that exists for layout work
    // cannot reach the Arabic shop it ships specifically to exercise RTL.
    if (import.meta.env.VITE_PREVIEW === '1') return;

    // Also an RPC: the browser holds no write grant on the control plane.
    const { error } = await supabase.schema('public')
      .rpc('set_my_language', { p_language: code });
    if (error) {
      // supabase-js returns the error rather than throwing, so this used to be
      // invisible: the language flipped, `load()` flipped it back a moment
      // later, and nothing said why. Same shape as the verdict save that drew
      // as decided without reaching the server.
      setOutcome({ kind: 'ready', session: { ...session, language: previous } });
      setLanguageError(error.message);
      return;
    }
    void load();
  }, [session, load]);

  const switchShop = useCallback((shopId: string) => {
    try { localStorage.setItem(PREFERRED_SHOP_KEY, shopId); } catch { /* private mode */ }
    setPreferredShopId(shopId);
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    setOutcome({ kind: 'signed_out' });
  }, []);

  const value = useMemo<SessionContextValue>(
    () => ({ outcome, loading, session, t, money, hours, setLanguage, languageError,
             switchShop, signOut }),
    [outcome, loading, session, t, money, hours, setLanguage, languageError,
     switchShop, signOut],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
